import React, { useState } from 'react'
import {
  Button,
  ToastNotifications,
  Modal,
  ModalHeader,
  ModalContent,
  ModalActions,
  Box,
  Text,
} from '@lightspeed/design-system-react'
import { ApiCustomer } from '@vend/types'
import { useT } from '@transifex/react'
import { AddressDetails } from '@vend/business-components/dist/components/AddressForm/types'
import { StepBreadcrumbs } from '../StepBreadcrumbs'
import { CustomerInfo, PartialCustomerGroup } from '../../../common/types'
import { STEPS } from '../types'
import { trimContactInfo } from '../../../common/customer-contact-utils'
import {
  useSaleInventoryData,
  useSaleManager,
  useCustomerManager,
  useVdFeatureManager,
  useSaleTracker,
} from '../../../../../../ng-compat/hooks'
import {
  EVENT,
  FEATURE_CAPTURE_ADDRESS_SELL_SCREEN,
} from '../../../../../../constants'
import {
  setFulfillmentOutletID,
  updateLineItems,
  selectLineItems,
  selectSaleLocalId,
  selectSaleCustomer,
  setSaleFulfilmentDetail,
  selectSaleFulfilmentDetail,
} from '../../../../../../state/slices/currentSale/currentSaleSlice'
import { useAppDispatch, useAppSelector } from '../../../../../../state/hooks'
import {
  FulfilmentDetail,
  FulfilmentType,
  isNoteInvalid,
} from '../../../../../../models/fulfilment'
import { LineItemFulfilmentNote } from '../../../../../sale/sale-line-item/fulfilment/LineItemFulfilmentNote'
import { getFeatureFromCache } from '../../../../../../util/feature-cache'
import { LineItemFulfilmentAddress } from '../../../../../sale/sale-line-item/fulfilment/LineItemFulfilmentAddress'
import {
  AddressErrorsStatus,
  validateFulfillmentAddresses,
  generateAddressDetailsFromCustomer,
} from '../../../../../sale/sale-line-item/fulfilment/popover/utils/fulfillmentHelper'
import { FormButtonOnEmptyShippingAddress } from '../../../../../sale/sale-line-item/fulfilment/address/FormButtonOnEmptyShippingAddress'

const CAN_NOT_SAVE_CUSTOMER = 'can not save customer'

interface Props {
  onStepChange: any
  onClose: (hasMarked: boolean) => void
  handleSaleNoteChange: (note: string) => void
  type: FulfilmentType
  newCustomer: CustomerInfo | null
  selectedCustomer: ApiCustomer | null
  group?: PartialCustomerGroup
  fulfillmentOutletID: string | null
  contactChanged: boolean
  note: string
  totalQuantity: number
}

// Convert added Customer to ApiCustomer format
const convertCustomerToApiCustomer = (
  customer: CustomerInfo | null,
  group: PartialCustomerGroup
) => {
  if (customer) {
    const result = {
      first_name: customer.firstName && customer.firstName.trim(),
      last_name: customer.lastName && customer.lastName.trim(),
      mobile: customer.mobile,
      email: customer.email,
      phone: customer.phone,
      fax: customer.fax,
      customer_group_id: group.id,
    } as ApiCustomer
    trimContactInfo(result)
    return result
  }
  return null
}

export const Step3: React.FC<Props> = ({
  onStepChange,
  onClose,
  handleSaleNoteChange,
  type,
  newCustomer,
  selectedCustomer,
  group,
  fulfillmentOutletID,
  contactChanged,
  note,
  totalQuantity,
}) => {
  const saleManager = useSaleManager()
  const customerManager = useCustomerManager()
  const saleInventoryData = useSaleInventoryData()
  const vdFeatureManager = useVdFeatureManager()
  const saleTracker = useSaleTracker()
  const dispatch = useAppDispatch()
  const saleFulfilmentDetail = useAppSelector(selectSaleFulfilmentDetail)
  const lineItems = useAppSelector(selectLineItems)
  const localId = useAppSelector(selectSaleLocalId)
  const saleCustomer = useAppSelector(selectSaleCustomer)
  let customerCreated = false

  const selectedCustomerAddress = selectedCustomer
    ? generateAddressDetailsFromCustomer(selectedCustomer)
    : undefined

  const [localFulfilmentDetails, setLocalFulfillmentDetails] = useState<
    FulfilmentDetail[]
  >(
    selectedCustomerAddress
      ? [{ ...saleFulfilmentDetail, ...selectedCustomerAddress }]
      : [saleFulfilmentDetail]
  )

  const shouldSaveSelectedOutletID = vdFeatureManager.get(
    'fulfill_from_another_outlet'
  )

  const [loading, setLoading] = useState<boolean>(false)

  const handleBillingAddressForShippingAddress = () =>
    handleMark(localFulfilmentDetails?.[0]?.billingAddress)

  const handleMark = (
    shippingAddress?: AddressDetails,
    billingAddress?: AddressDetails
  ) => {
    const customerPromise = selectedCustomer
      ? handleCustomerUpdate
      : handleCustomerCreate

    customerPromise()
      .then(() => {
        markUnfulfilled()
        dispatch(
          updateLineItems(
            lineItems.map(item => ({
              ...item,
              fulfilmentType: type,
            }))
          )
        )
        const newFulfillmentDetails = {
          type,
          note,
          billingAddress:
            billingAddress || localFulfilmentDetails?.[0]?.billingAddress,
          shippingAddress:
            shippingAddress || localFulfilmentDetails?.[0]?.shippingAddress,
        }

        dispatch(setSaleFulfilmentDetail(newFulfillmentDetails))

        if (isCaptureAddressSellScreenEnabled)
          saleManager.updateFulfillmentDetails([newFulfillmentDetails])

        saleManager.saveSale()
        saleTracker.addTrackingData({
          customerCreated,
          saleId: localId,
        })

        ToastNotifications.success(
          type === FulfilmentType.DELIVERY
            ? t(
                '{totalQuantity, plural, one {{totalQuantity} item is marked for delivery} other {All {totalQuantity} items are marked for delivery}}',
                {
                  _context: 'Mark as unfulfilled modal - success notification',
                  totalQuantity,
                }
              )
            : t(
                '{totalQuantity, plural, one {{totalQuantity} item is marked for pickup} other {All {totalQuantity} items are marked for pickup}}',
                {
                  _context: 'Mark as unfulfilled modal - success notification',
                  totalQuantity,
                }
              )
        )

        onClose(true)
      })
      .catch(error => {
        setLoading(false)
        if (error && error !== CAN_NOT_SAVE_CUSTOMER) {
          ToastNotifications.negative(
            t('Failed to mark as unfulfilled, please try again.', {
              _context: 'Mark as unfulfilled modal - error notification',
            })
          )
        }
      })
  }

  const markUnfulfilled = () => {
    if (type === FulfilmentType.DELIVERY) {
      saleManager.unfulfilledDelivery()
    } else {
      saleManager.unfulfilledPickUp()
    }
    saleInventoryData.emit(EVENT.LINE_ITEM_UPDATE_VALIDATION)

    if (shouldSaveSelectedOutletID && fulfillmentOutletID) {
      dispatch(setFulfillmentOutletID(fulfillmentOutletID))
    }
  }
  const handleCustomerCreate = () => {
    return new Promise<void>((resolve, reject) => {
      const customer = convertCustomerToApiCustomer(newCustomer, group!)
      if (customer) {
        setLoading(true)
        return customerManager
          .saveNewCustomer(customer)
          .then(savedCustomer => {
            customerCreated = true
            return saleManager.addCustomer(savedCustomer)
          })
          .then(() => resolve())
          .catch(() => reject(CAN_NOT_SAVE_CUSTOMER))
      }
    })
  }

  const handleCustomerUpdate = () => {
    return new Promise<void>((resolve, reject) => {
      setLoading(true)
      if (contactChanged) {
        trimContactInfo(selectedCustomer!)
        return customerManager
          .updateCustomer(selectedCustomer!)
          .then(savedCustomer => saleManager.addCustomer(savedCustomer))
          .then(() => resolve())
          .catch(() => reject(CAN_NOT_SAVE_CUSTOMER))
      } else if (saleCustomer && saleCustomer.id === selectedCustomer!.id) {
        // Didn't change pre-attached customer
        return resolve()
      } else {
        // Selected/changed a customer at Step2, but didn't update contact info
        return saleManager.addCustomer(selectedCustomer!).then(() => resolve())
      }
    })
  }

  const handleSaveAddress = (fulfillmentDetails: FulfilmentDetail[]) => {
    setLocalFulfillmentDetails(fulfillmentDetails)
  }

  const { error, status } = validateFulfillmentAddresses(
    localFulfilmentDetails?.[0]
  )

  const isCaptureAddressSellScreenEnabled = getFeatureFromCache(
    FEATURE_CAPTURE_ADDRESS_SELL_SCREEN
  )

  const shouldShowFulfilmentAddress =
    isCaptureAddressSellScreenEnabled && type === FulfilmentType.DELIVERY

  const t = useT()

  return (
    <Modal size="small" onClose={() => onClose(false)}>
      <StepBreadcrumbs
        step={STEPS.DETAILS}
        isAddingCustomer={Boolean(newCustomer)}
      />
      <ModalHeader>
        {t(
          '{totalQuantity, plural, one {Add fulfillment details for the {totalQuantity} item} other {Add fulfillment details for these {totalQuantity} items}}',
          {
            _context: 'Mark as unfulfilled modal - header',
            totalQuantity,
          }
        )}
        <Text tag="p" className="vd-mt2">
          {t(
            '{totalQuantity, plural, one {Record the fulfillment details for the item in the fulfillment note.} other {Record the fulfillment details for these items in the fulfillment note.}}',
            {
              _context: 'Mark as unfulfilled modal - subheader',
              totalQuantity,
            }
          )}
        </Text>
      </ModalHeader>

      <ModalContent>
        {shouldShowFulfilmentAddress && (
          <div className="vd-mb8">
            <LineItemFulfilmentAddress
              fulfillmentDetail={localFulfilmentDetails?.[0]}
              isModalOpeningOnError={true}
              onSave={
                isCaptureAddressSellScreenEnabled
                  ? handleSaveAddress
                  : undefined
              }
            />
          </div>
        )}

        <LineItemFulfilmentNote
          note={note}
          onChange={handleSaleNoteChange}
          isDisabled={loading}
        />
      </ModalContent>
      <ModalActions>
        <Box className="vd-btn-group">
          <Button
            onClick={() =>
              onStepChange(newCustomer ? STEPS.ADD : STEPS.CUSTOMER)
            }
            variant="supplementary"
            disabled={loading}
            data-track={newCustomer ? 'back-add-step' : 'back-customer-step'}
            modifier={
              status &&
              (status === AddressErrorsStatus.EMPTYSHIPPING_CORRECTBILLING ||
                status === AddressErrorsStatus.EMPTYSHIPPING_INCORRECTBILLING)
                ? 'text'
                : undefined
            }
          >
            {t('Back', {
              _context: 'Mark as unfulfilled modal - back button',
            })}
          </Button>
          {isCaptureAddressSellScreenEnabled &&
          type === FulfilmentType.DELIVERY &&
          (status === AddressErrorsStatus.EMPTYSHIPPING_CORRECTBILLING ||
            status === AddressErrorsStatus.EMPTYSHIPPING_INCORRECTBILLING) ? (
            <FormButtonOnEmptyShippingAddress
              loading={loading}
              onClose={onClose}
              fulfilmentDetail={localFulfilmentDetails[0]}
              handleEmptyShippingCorrectBillingAddress={
                handleBillingAddressForShippingAddress
              }
              onSave={fulfillmentDetails => {
                handleSaveAddress(fulfillmentDetails)
                handleMark(
                  fulfillmentDetails?.[0]?.shippingAddress,
                  fulfillmentDetails?.[0]?.billingAddress
                )
              }}
            />
          ) : (
            <Button
              onClick={() => handleMark()}
              variant="go"
              loading={loading}
              disabled={
                isNoteInvalid(note) ||
                (isCaptureAddressSellScreenEnabled &&
                  type === FulfilmentType.DELIVERY &&
                  Boolean(error))
              }
              data-track={'mark-items-for-fulfillment'}
            >
              {t('Mark items for fulfillment', {
                _context: 'Mark as unfulfilled modal - next button',
              })}
            </Button>
          )}
        </Box>
      </ModalActions>
    </Modal>
  )
}
